import React, { useState } from 'react';
import './BetSlip.css';
import { FaBullseye, FaFutbol, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

export default function BetSlip({
  bets,
  balance,
  onRemove,
  onUpdateStake,
  onPlace,          // placeBet({ stake, items }) from authContext
  onBetsCleared,    // called after successful bet
  onNeedAuth,
  onNeedDeposit,
}) {
  const [betType, setBetType]     = useState('single');
  const [comboStake, setComboStake] = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);

  const totalOdds = bets.reduce((acc, b) => acc * parseFloat(b.odds), 1);

  const totalStake = betType === 'single'
    ? bets.reduce((acc, b) => acc + (parseFloat(b.stake) || 0), 0)
    : parseFloat(comboStake) || 0;

  const potentialWin = betType === 'single'
    ? bets.reduce((acc, b) => acc + (parseFloat(b.stake) || 0) * parseFloat(b.odds), 0)
    : totalStake * totalOdds;

  const handlePlace = async () => {
    setError(null);
    setSuccess(null);

    // Build items array
    const items = bets
      .filter((b) => b.fixture_id && b.odd_id)
      .map((b) => ({ fixture_id: b.fixture_id, odd_id: b.odd_id }));

    if (items.length === 0) {
      setError('Aucun pari valide dans le ticket.');
      return;
    }

    // Validate stakes
    if (betType === 'single') {
      const missing = bets.find((b) => !parseFloat(b.stake));
      if (missing) { setError('Veuillez entrer une mise pour chaque pari.'); return; }
    } else {
      if (!parseFloat(comboStake)) { setError('Veuillez entrer votre mise.'); return; }
    }

    if (totalStake <= 0) { setError('La mise doit être supérieure à 0.'); return; }

    // Frontend balance check — avoid unnecessary API call
    if (balance < totalStake) {
      setError('Solde insuffisant. Veuillez effectuer un dépôt.');
      setTimeout(() => onNeedDeposit?.(), 1500);
      return;
    }

    setLoading(true);
    try {
      if (betType === 'single') {
        // Place each bet individually
        for (const bet of bets) {
          const stake = parseFloat(bet.stake);
          await onPlace({
            stake,
            items: [{ fixture_id: bet.fixture_id, odd_id: bet.odd_id }],
          });
        }
      } else {
        // Combo: all items in one ticket
        await onPlace({ stake: parseFloat(comboStake), items });
      }
      setSuccess('Paris placés avec succès ! ');
      setComboStake('');
      setTimeout(() => {
        setSuccess(null);
        onBetsCleared?.();
      }, 2000);
    } catch (err) {
      const msg = err.message || '';
      if (msg === 'INSUFFICIENT_BALANCE' || msg.toLowerCase().includes('solde')) {
        setError('Solde insuffisant. Veuillez effectuer un dépôt.');
        setTimeout(() => onNeedDeposit?.(), 1500);
      } else if (msg.toLowerCase().includes('started') || msg.toLowerCase().includes('commencé')) {
        setError('Un match a déjà commencé. Il a été retiré de votre ticket.');
      } else {
        setError(msg || 'Une erreur est survenue.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside className="betslip">
      <div className="betslip-header">
        <div className="betslip-title">
          <FaBullseye />
          <h3>Ticket de paris</h3>
        </div>
        {bets.length > 0 && <span className="betslip-count">{bets.length}</span>}
      </div>

      {bets.length === 0 ? (
        <div className="betslip-empty">
          <span className="empty-icon"><FaFutbol /></span>
          <p>Sélectionnez des cotes pour ajouter des paris</p>
        </div>
      ) : (
        <>
          {bets.length > 1 && (
            <div className="bet-type-tabs">
              <button className={`bet-type-tab ${betType === 'single' ? 'active' : ''}`} onClick={() => setBetType('single')}>
                Simples
              </button>
              <button className={`bet-type-tab ${betType === 'combo' ? 'active' : ''}`} onClick={() => setBetType('combo')}>
                Combiné
              </button>
            </div>
          )}

          <div className="betslip-bets">
            {bets.map((bet) => (
              <div key={bet.id} className="betslip-bet">
                <div className="bet-top">
                  <div className="bet-info">
                    <span className="bet-match">{bet.match}</span>
                    <span className="bet-selection">{bet.selection}</span>
                  </div>
                  <button className="bet-remove" onClick={() => onRemove(bet.id)}>×</button>
                </div>
                <div className="bet-odds-row">
                  <span className="bet-market">{bet.market}</span>
                  <span className="bet-odds">{parseFloat(bet.odds).toFixed(2)}</span>
                </div>
                {betType === 'single' && (
                  <div className="bet-stake-row">
                    <input
                      type="number"
                      className="stake-input"
                      placeholder="Mise (€)"
                      min="0"
                      value={bet.stake}
                      onChange={(e) => onUpdateStake(bet.id, e.target.value)}
                    />
                    {bet.stake && (
                      <span className="win-estimate">
                        Gain: <strong>{(parseFloat(bet.stake) * parseFloat(bet.odds)).toFixed(2)} FC</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {betType === 'combo' && (
            <div className="combo-stake">
              <div className="combo-odds">
                <span>Cote combinée</span>
                <strong>{totalOdds.toFixed(2)}</strong>
              </div>
              <input
                type="number"
                className="stake-input full"
                placeholder="Mise combinée (€)"
                min="0"
                value={comboStake}
                onChange={(e) => setComboStake(e.target.value)}
              />
            </div>
          )}

          <div className="betslip-summary">
            <div className="summary-row">
              <span>Solde disponible</span>
              <span className={balance < totalStake && totalStake > 0 ? 'balance-warn' : ''}>
                {balance.toLocaleString('fr-FR')} FC
              </span>
            </div>
            <div className="summary-row">
              <span>Mise totale</span>
              <span>{totalStake.toFixed(2)} FC</span>
            </div>
            <div className="summary-row gain">
              <span>Gain potentiel</span>
              <strong>{potentialWin.toFixed(2)} FC</strong>
            </div>
          </div>

          {error   && <div className="betslip-error"><FaExclamationTriangle /> {error}</div>}
          {success && <div className="betslip-success"><FaCheckCircle /> {success}</div>}

          <button
            className={`place-bet-btn ${loading ? 'loading' : ''}`}
            onClick={handlePlace}
            disabled={loading}
          >
            {loading ? <><span className="betslip-spinner" /> En cours...</> : 'Placer les paris'}
          </button>
        </>
      )}

      <div className="betslip-quick-stakes">
        <p className="quick-title">Mises rapides</p>
        <div className="quick-buttons">
          {[10000, 15000, 20000, 50000, 100000].map((amount) => (
            <button
              key={amount}
              className="quick-btn"
              onClick={() => {
                if (bets.length > 0) {
                  if (betType === 'combo') setComboStake(String(amount));
                  else onUpdateStake(bets[bets.length - 1].id, String(amount));
                }
              }}
            >
              {amount >= 1000 ? `${amount / 1000}K` : amount} FC
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
